import { FOLLOW_UP_OUTCOME_KINDS, type FollowUpOutcomeKind } from '@sigora/domain';
import { useId } from 'react';

import {
  FOLLOW_UP_CHOICE_LABELS,
  FOLLOW_UP_RETRY_LABEL,
  FOLLOW_UP_SUGGESTED_SUFFIX,
  followUpChoicePrompt,
} from '../brief-copy.ts';

export interface OutcomeChoiceProps {
  /** The research question of the card the answer relates to, or `null` when it names none. */
  card: string | null;
  /** The outcome Sigora suggests, drawn as the primary button. */
  suggested: FollowUpOutcomeKind;
  /** Whether a choice is on its way, which holds every button until it answers. */
  busy: boolean;
  /** Whether an update of the Brief failed, which makes its button the Try again. */
  updateFailed: boolean;
  onChoose: (kind: FollowUpOutcomeKind) => void;
}

/**
 * How an answer lands: the three outcomes, every one always offered, the one Sigora suggests drawn
 * as the primary button and named so to a screen reader.
 */
export const OutcomeChoice = ({
  card,
  suggested,
  busy,
  updateFailed,
  onChoose,
}: OutcomeChoiceProps) => {
  const promptId = useId();

  return (
    <div className="follow-up-choice" role="group" aria-labelledby={promptId}>
      <p id={promptId} data-test-id="follow-up-choice-prompt">
        {followUpChoicePrompt(card)}
      </p>
      <div className="follow-up-choice-buttons">
        {FOLLOW_UP_OUTCOME_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            className={kind === suggested ? 'button-primary' : undefined}
            data-test-id="follow-up-choice"
            data-kind={kind}
            data-suggested={String(kind === suggested)}
            disabled={busy}
            onClick={() => onChoose(kind)}
          >
            {kind === 'update_brief' && updateFailed
              ? FOLLOW_UP_RETRY_LABEL
              : FOLLOW_UP_CHOICE_LABELS[kind]}
            {kind === suggested && (
              <span className="visually-hidden">{FOLLOW_UP_SUGGESTED_SUFFIX}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
